import { computed, inject, Injectable, signal, Signal } from '@angular/core';
import { AppEvent } from '@app/shared/types/models/notifications/app-event';
import { TaskDescriptionAcknowledgement } from '@app/shared/types/models/task/task-description-acknowledgement.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { filter, map, Observable, tap } from 'rxjs';
import { DataService } from './data-service';
import { TaskDescriptionAcknowledgementDTO } from './dtos/project-tasks/task-description-acknowledgement.dto';
import { TaskDescriptionAcknowledgementFactory } from './factories/task-description-acknowledgement.factory';
import { TaskDescriptionAcknowledgementsHttpService } from './http/task-description-acknowledgements-http.service';

/**
 * TaskDescriptionAcknowledgementsDataService - Manages task description acknowledgement data, caching, and store operations.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Consumed by {@link TaskDescriptionAcknowledgementsActionsService} or components that display or mutate description acknowledgements.
 *
 * Architecture:
 * - {@link TaskDescriptionAcknowledgementsDataService}: Data access, queries, store management
 * - {@link TaskDescriptionAcknowledgementsHttpService}: HTTP API calls
 */
@Injectable({ providedIn: 'root' })
export class TaskDescriptionAcknowledgementsDataService extends DataService<
  TaskDescriptionAcknowledgementDTO,
  TaskDescriptionAcknowledgement
> {
  private readonly acknowledgements = new DataStore<TaskDescriptionAcknowledgement>();
  private readonly acknowledgementIdsByTask = new DataStore<IdsCollection>();

  private readonly factory = inject(TaskDescriptionAcknowledgementFactory);
  private readonly http = inject(TaskDescriptionAcknowledgementsHttpService);

  public getById(id: string): Signal<TaskDescriptionAcknowledgement> {
    return this.acknowledgements.get(id)!;
  }

  public upsertLocalData(dto: TaskDescriptionAcknowledgementDTO): Signal<TaskDescriptionAcknowledgement> {
    const acknowledgement = this.factory.produce(dto);
    return acknowledgement ? this.acknowledgements.upsert(acknowledgement)! : signal(undefined);
  }

  protected handleEvent(event: AppEvent): void {
    if (event.eventType !== 'TaskDataChanged' && event.eventType !== 'TaskGroupChanged') {
      return;
    }

    const taskId = event.data as string;
    if (!taskId || !this.acknowledgementIdsByTask.hasDataForId(taskId)) {
      return;
    }

    this.reloadAcknowledgements(taskId);
  }

  public getAcknowledgements(taskId: string): Signal<TaskDescriptionAcknowledgement[]> {
    if (!this.acknowledgementIdsByTask.hasDataForId(taskId)) {
      this.acknowledgementIdsByTask.upsert({
        id: taskId,
        ids: []
      });
      this.fetchAcknowledgements(taskId);
    }

    return computed(() => {
      const ids = [...new Set(this.acknowledgementIdsByTask.get(taskId)()?.ids ?? [])];
      return ids
        .map(acknowledgementId => this.acknowledgements.get(acknowledgementId)?.())
        .filter(
          (acknowledgement): acknowledgement is TaskDescriptionAcknowledgement =>
            acknowledgement != null && acknowledgement.id != null
        )
        .sort((a, b) => (a.acceptedAt?.getTime() ?? 0) - (b.acceptedAt?.getTime() ?? 0));
    });
  }

  public reloadAcknowledgements(taskId: string): void {
    this.fetchAcknowledgements(taskId);
  }

  private fetchAcknowledgements(taskId: string): void {
    this.http.getAcknowledgements(taskId).subscribe(dtos => {
      this.applyAcknowledgementDtos(taskId, dtos);
    });
  }

  public createAcknowledgement(taskId: string): Observable<TaskDescriptionAcknowledgement> {
    return this.http.createAcknowledgement(taskId).pipe(
      map(dto => this.factory.produce(dto)),
      filter((acknowledgement): acknowledgement is TaskDescriptionAcknowledgement => acknowledgement != null),
      tap(acknowledgement => {
        this.acknowledgements.upsert(acknowledgement);

        const collection = this.acknowledgementIdsByTask.get(taskId)?.();
        const existingIds = collection?.ids ?? [];
        const nextIds = existingIds.includes(acknowledgement.id) ? existingIds : [...existingIds, acknowledgement.id];

        this.acknowledgementIdsByTask.upsert({
          id: taskId,
          ids: nextIds
        });

        this.notificationService.notify({
          eventType: 'TaskDescriptionAcknowledgementCreated',
          data: { taskId, acknowledgement }
        });
      })
    );
  }

  private applyAcknowledgementDtos(taskId: string, dtos: TaskDescriptionAcknowledgementDTO[]): void {
    const models = dtos
      .map(dto => this.factory.produce(dto))
      .filter((acknowledgement): acknowledgement is TaskDescriptionAcknowledgement => acknowledgement != null);

    if (models.length > 0) {
      this.acknowledgements.upsertMany(models);
    }

    this.acknowledgementIdsByTask.upsert({
      id: taskId,
      ids: [...new Set(dtos.map(dto => dto.id))]
    });
  }
}
